/**
 * Thin fetch wrapper. The API is same-origin, so the session cookie rides along
 * automatically and there is no token to store in JavaScript.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly details?: { path: string; message: string }[],
    /**
     * The decoded error body, whole. Almost every failure needs nothing beyond
     * the message, but a few carry structured data the caller has to act on:
     * a 409 from the meetings API returns the clashes that blocked the save,
     * and the "book anyway" prompt is built from them. Optional and last, so
     * every existing construction and every existing reader is unaffected.
     */
    readonly body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type Options = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  signal?: AbortSignal
}

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers: options.body ? { 'Content-Type': 'application/json' } : undefined,
    body: options.body ? JSON.stringify(options.body) : undefined,
    credentials: 'same-origin',
    signal: options.signal,
  })

  if (res.status === 204) return undefined as T

  const text = await res.text()
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {}

  if (!res.ok) {
    const details = payload.details as { path: string; message: string }[] | undefined
    // Field-level validation messages are more useful than the generic summary.
    const message =
      details?.length && details[0]
        ? details[0].message
        : ((payload.error as string) ?? `Request failed (${res.status})`)
    throw new ApiError(res.status, message, payload.code as string | undefined, details, payload)
  }

  return payload as T
}

/** Builds a querystring, dropping empty values so URLs stay readable. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  )
  if (entries.length === 0) return ''
  return `?${entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&')}`
}
